import React, { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalCloseButton,
    Accordion,
    AccordionItem,
    AccordionButton,
    AccordionPanel,
    VStack,
    HStack,
    Icon,
    Text,
    Box,
    Image,
    SimpleGrid,
    Input,
    Textarea,
    Button,
    FormLabel,
    FormControl,
    Badge,
    useToast,
    Spinner,
    Skeleton,
    IconButton,
    Tabs,
    TabList,
    TabPanels,
    Tab,
    TabPanel,
    Flex,
    Tooltip,
    useDisclosure,
} from "@chakra-ui/react";
import {
    IoCalendarOutline,
    IoCheckmarkCircle,
    IoCloudUploadOutline,
    IoClose,
    IoStorefrontOutline,
    IoLocationOutline,
    IoPeopleOutline,
    IoResizeOutline,
    IoPricetagOutline,
    IoImagesOutline,
    IoTimerOutline,
    IoCashOutline,
    IoInformationCircleOutline,
    IoWarningOutline,
    IoChevronBack,
    IoChevronForward,
    IoOpenOutline,
    IoCallOutline,
    IoMailOutline,
    IoSparklesOutline,
    IoExpandOutline,
    IoLockClosedOutline,
} from "react-icons/io5";
import { useJsApiLoader, Autocomplete, GoogleMap, MarkerF } from "@react-google-maps/api";
import { useNavigate } from "react-router-dom";
import { collection, setDoc, getDocs, query, where, doc, updateDoc } from "firebase/firestore";
import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { db } from "../../firebase/firebase-config";
import { saveEventDraft, loadEventDraft, deleteEventDraft } from "../../FBFunctions/eventFunctions";
import { RichTextEditor } from "../Structure/RichTextEditor";
import { InterestPicker } from "../Structure/InterestPicker";
// Lifts Google Places' `.pac-container` above the Chakra modal so the
// autocomplete dropdown is actually visible on the Custom Place tab.
import "./createEvent/googlePlaces.css";

// Pure helpers, schema, and payload builders live in their own folder so
// this file can stay focused on UI, effects, and event handlers.
import {
    GOOGLE_LIBRARIES,
    MAP_CONTAINER_STYLE,
    MAP_OPTIONS,
    DEFAULT_FORM_DATA,
    SECTIONS,
    formatMoney,
    generateEventId,
    toUrlSafeEventId,
    toDateInputString,
    toTimeInputString,
    computeEventHours,
    requiredFieldsFilled,
    buildSharedEventFields,
    buildCreateEventPayload,
    isEventApproved,
    isEventConfirmed,
} from "./createEvent";
import {
    GOOGLE_MAPS_API_KEY,
    GOOGLE_MAPS_LOADER_ID,
} from "../../lib/googleMaps";

// ─── Venue preview hero gallery ──────────────────────────────────────────────
// Swipe-able cover image for the partner-venue preview. Falls back to the
// brand-blue header block when the venue has no images. Renders prev/next
// arrows + a counter chip + dot pagination only when there are 2+ images,
// so the single-image case stays visually identical to before.
const VenuePreviewGallery = ({ images, venueName }) => {
    const safeImages = Array.isArray(images)
        ? images.filter((img) => img && (img.url || typeof img === "string"))
        : [];
    const [activeIdx, setActiveIdx] = useState(0);
    const [lightboxOpen, setLightboxOpen] = useState(false);

    // Defensively reset when the venue changes (different lengths).
    useEffect(() => {
        setActiveIdx(0);
        setLightboxOpen(false);
    }, [safeImages.length, venueName]);

    // Keyboard nav inside the lightbox: ←/→ to navigate, Esc to close.
    useEffect(() => {
        if (!lightboxOpen) return undefined;
        const onKey = (e) => {
            if (e.key === "Escape") setLightboxOpen(false);
            else if (e.key === "ArrowLeft")
                setActiveIdx((i) => (i - 1 + safeImages.length) % safeImages.length);
            else if (e.key === "ArrowRight")
                setActiveIdx((i) => (i + 1) % safeImages.length);
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [lightboxOpen, safeImages.length]);

    if (safeImages.length === 0) {
        return (
            <div style={{ backgroundColor: "#508cf7", padding: "24px 20px", flexShrink: 0 }}>
                <Badge bg="whiteAlpha.300" color="white" fontSize="10px" borderRadius="full" px={2} mb={1}>
                    PARTNER VENUE
                </Badge>
                <Text color="white" fontWeight="700" fontSize="xl">
                    {venueName}
                </Text>
            </div>
        );
    }

    const total = safeImages.length;
    const current = safeImages[activeIdx] || safeImages[0];
    const currentUrl = typeof current === "string" ? current : current.url;
    const goPrev = () => setActiveIdx((i) => (i - 1 + total) % total);
    const goNext = () => setActiveIdx((i) => (i + 1) % total);

    return (
        <div style={{ height: "220px", position: "relative", flexShrink: 0, background: "#0f172a" }}>
            <img
                src={currentUrl}
                alt={`${venueName} — ${activeIdx + 1} of ${total}`}
                onClick={() => setLightboxOpen(true)}
                style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    display: "block",
                    transition: "opacity 0.2s",
                    cursor: "zoom-in",
                }}
            />

            {/* Expand to full view */}
            <button
                type="button"
                aria-label="View full size"
                onClick={(e) => {
                    e.stopPropagation();
                    setLightboxOpen(true);
                }}
                style={{
                    position: "absolute",
                    bottom: 12,
                    right: 12,
                    height: 32,
                    padding: "0 14px",
                    borderRadius: "999px",
                    background: "white",
                    color: "#226cf1",
                    border: "none",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    fontSize: "11px",
                    fontWeight: 700,
                    letterSpacing: "0.04em",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.25)",
                    zIndex: 3,
                }}
            >
                <IoExpandOutline size={14} />
                View
            </button>

            {/* Bottom title gradient */}
            <div
                style={{
                    position: "absolute",
                    inset: 0,
                    background:
                        "linear-gradient(to top, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0.15) 45%, transparent 65%)",
                    pointerEvents: "none",
                }}
            />

            {/* Title block */}
            <div style={{ position: "absolute", bottom: 16, left: 20, right: 20, pointerEvents: "none" }}>
                <Badge
                    colorScheme="blue"
                    bg="whiteAlpha.900"
                    color="#508cf7"
                    fontSize="10px"
                    borderRadius="full"
                    px={2}
                    mb={1}
                >
                    PARTNER VENUE
                </Badge>
                <Text color="white" fontWeight="700" fontSize="xl" lineHeight="1.2">
                    {venueName}
                </Text>
            </div>

            {total > 1 && (
                <>
                    {/* Counter chip */}
                    <div
                        style={{
                            position: "absolute",
                            top: 12,
                            left: 12,
                            background: "rgba(0,0,0,0.55)",
                            color: "white",
                            fontSize: "11px",
                            fontWeight: 600,
                            padding: "4px 10px",
                            borderRadius: "999px",
                            letterSpacing: "0.04em",
                        }}
                    >
                        {activeIdx + 1} / {total}
                    </div>

                    {/* Prev / next */}
                    <button
                        type="button"
                        aria-label="Previous photo"
                        onClick={(e) => {
                            e.stopPropagation();
                            goPrev();
                        }}
                        style={{
                            position: "absolute",
                            top: "50%",
                            left: 8,
                            transform: "translateY(-50%)",
                            width: 32,
                            height: 32,
                            borderRadius: "50%",
                            background: "rgba(0,0,0,0.5)",
                            color: "white",
                            border: "none",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        <IoChevronBack size={16} />
                    </button>
                    <button
                        type="button"
                        aria-label="Next photo"
                        onClick={(e) => {
                            e.stopPropagation();
                            goNext();
                        }}
                        style={{
                            position: "absolute",
                            top: "50%",
                            right: 8,
                            transform: "translateY(-50%)",
                            width: 32,
                            height: 32,
                            borderRadius: "50%",
                            background: "rgba(0,0,0,0.5)",
                            color: "white",
                            border: "none",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        <IoChevronForward size={16} />
                    </button>

                    {/* Dot pagination */}
                    <div
                        style={{
                            position: "absolute",
                            top: 12,
                            right: 56, /* leave room for the close button */
                            display: "flex",
                            gap: 4,
                            padding: "4px 8px",
                            borderRadius: "999px",
                            background: "rgba(0,0,0,0.35)",
                        }}
                    >
                        {safeImages.map((_, i) => (
                            <button
                                key={i}
                                type="button"
                                aria-label={`Go to photo ${i + 1}`}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setActiveIdx(i);
                                }}
                                style={{
                                    width: i === activeIdx ? 16 : 6,
                                    height: 6,
                                    borderRadius: 999,
                                    background:
                                        i === activeIdx ? "white" : "rgba(255,255,255,0.55)",
                                    border: "none",
                                    cursor: "pointer",
                                    padding: 0,
                                    transition: "width 0.2s",
                                }}
                            />
                        ))}
                    </div>
                </>
            )}

            {/* Fullscreen lightbox — portalled to <body> to escape parent modal */}
            {lightboxOpen && createPortal(
                <div
                    onClick={() => setLightboxOpen(false)}
                    style={{
                        position: "fixed",
                        inset: 0,
                        zIndex: 10000,
                        background: "rgba(0,0,0,0.92)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: "1rem",
                        overscrollBehavior: "contain",
                    }}
                >
                    <img
                        src={currentUrl}
                        alt={`${venueName} — full size ${activeIdx + 1} of ${total}`}
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            maxWidth: "100%",
                            maxHeight: "100%",
                            objectFit: "contain",
                            borderRadius: 8,
                            boxShadow: "0 25px 50px -12px rgba(0,0,0,0.6)",
                        }}
                    />

                    {/* Close */}
                    <button
                        type="button"
                        aria-label="Close full view"
                        onClick={(e) => {
                            e.stopPropagation();
                            setLightboxOpen(false);
                        }}
                        style={{
                            position: "absolute",
                            top: 16,
                            right: 16,
                            width: 40,
                            height: 40,
                            borderRadius: "50%",
                            background: "rgba(255,255,255,0.12)",
                            color: "white",
                            border: "none",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            backdropFilter: "blur(4px)",
                        }}
                    >
                        <IoClose size={20} />
                    </button>

                    {/* Counter */}
                    {total > 1 && (
                        <div
                            style={{
                                position: "absolute",
                                top: 20,
                                left: "50%",
                                transform: "translateX(-50%)",
                                background: "rgba(255,255,255,0.12)",
                                color: "white",
                                fontSize: "12px",
                                fontWeight: 600,
                                padding: "6px 12px",
                                borderRadius: "999px",
                                letterSpacing: "0.04em",
                                backdropFilter: "blur(4px)",
                            }}
                        >
                            {activeIdx + 1} / {total}
                        </div>
                    )}

                    {total > 1 && (
                        <>
                            <button
                                type="button"
                                aria-label="Previous photo"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    goPrev();
                                }}
                                style={{
                                    position: "absolute",
                                    top: "50%",
                                    left: 16,
                                    transform: "translateY(-50%)",
                                    width: 44,
                                    height: 44,
                                    borderRadius: "50%",
                                    background: "rgba(255,255,255,0.12)",
                                    color: "white",
                                    border: "none",
                                    cursor: "pointer",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    backdropFilter: "blur(4px)",
                                }}
                            >
                                <IoChevronBack size={22} />
                            </button>
                            <button
                                type="button"
                                aria-label="Next photo"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    goNext();
                                }}
                                style={{
                                    position: "absolute",
                                    top: "50%",
                                    right: 16,
                                    transform: "translateY(-50%)",
                                    width: 44,
                                    height: 44,
                                    borderRadius: "50%",
                                    background: "rgba(255,255,255,0.12)",
                                    color: "white",
                                    border: "none",
                                    cursor: "pointer",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    backdropFilter: "blur(4px)",
                                }}
                            >
                                <IoChevronForward size={22} />
                            </button>

                            {/* Thumbnail strip */}
                            <div
                                onClick={(e) => e.stopPropagation()}
                                style={{
                                    position: "absolute",
                                    bottom: 20,
                                    left: "50%",
                                    transform: "translateX(-50%)",
                                    display: "flex",
                                    gap: 8,
                                    padding: 8,
                                    borderRadius: 12,
                                    background: "rgba(255,255,255,0.08)",
                                    backdropFilter: "blur(4px)",
                                    maxWidth: "90vw",
                                    overflowX: "auto",
                                }}
                            >
                                {safeImages.map((img, i) => {
                                    const url = typeof img === "string" ? img : img.url;
                                    return (
                                        <button
                                            key={i}
                                            type="button"
                                            aria-label={`Show photo ${i + 1}`}
                                            onClick={() => setActiveIdx(i)}
                                            style={{
                                                width: 56,
                                                height: 56,
                                                borderRadius: 8,
                                                border:
                                                    i === activeIdx
                                                        ? "2px solid #508cf7"
                                                        : "2px solid transparent",
                                                padding: 0,
                                                cursor: "pointer",
                                                background: "#000",
                                                opacity: i === activeIdx ? 1 : 0.6,
                                                flexShrink: 0,
                                                overflow: "hidden",
                                            }}
                                        >
                                            <img
                                                src={url}
                                                alt={`Thumbnail ${i + 1}`}
                                                style={{
                                                    width: "100%",
                                                    height: "100%",
                                                    objectFit: "cover",
                                                    display: "block",
                                                }}
                                            />
                                        </button>
                                    );
                                })}
                            </div>
                        </>
                    )}
                </div>,
                document.body
            )}
        </div>
    );
};

// ─── Component ────────────────────────────────────────────────────────────────
export const CreateEventModal = ({ isOpen, onClose, user, event = null, onEventUpdated }) => {
    const toast = useToast();
    const navigate = useNavigate();
    const isEditMode = !!event;
    // Once a venue has approved an event (or there's no external venue
    // gate because the host is using their own venue / a custom place),
    // the location is locked in — guests have already been told where to
    // show up, and the venue booking terms are settled.
    const lockApprovedFields = isEditMode && isEventApproved(event);
    // Confirmed = approved + ticket minimum hit (or self-hosted). At that
    // point ticket price and attendee cap are also frozen because guests
    // have committed at those numbers.
    const lockPricingFields = isEditMode && isEventConfirmed(event);

    const [formData, setFormData] = useState(DEFAULT_FORM_DATA);
    const [completedSections, setCompletedSections] = useState(SECTIONS.map(() => false));
    const [openIndex, setOpenIndex] = useState(0);
    const [mapCenter, setMapCenter] = useState(null);
    const [partnerVenues, setPartnerVenues] = useState([]);
    const [loadingVenues, setLoadingVenues] = useState(false);
    const [locationTab, setLocationTab] = useState(0); // 0 = Partner, 1 = Custom
    const [previewVenue, setPreviewVenue] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [uploadingImage, setUploadingImage] = useState(false);
    const [draftLoading, setDraftLoading] = useState(false);
    const [isDirty, setIsDirty] = useState(false);

    const autocompleteRef = useRef(null);
    const fileInputRef = useRef(null);
    const modalBodyRef = useRef(null);

    const { isOpen: isVenueOpen, onOpen: onVenueOpen, onClose: onVenueClose } = useDisclosure();

    // Close the custom venue overlay on Escape.
    useEffect(() => {
        if (!isVenueOpen) return;
        const onKey = (e) => {
            if (e.key === "Escape") onVenueClose();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [isVenueOpen, onVenueClose]);

    // When the custom sub-modal is open we disabled Chakra's react-remove-scroll
    // wrapper (via blockScrollOnMount={!isVenueOpen}) so the sub-modal can
    // actually scroll its own content. In exchange, we have to manually lock
    // scrolling on:
    //   1) the page behind (document body + html element)
    //   2) the outer event-creation modal's own scrollable body
    // so nothing behind the sub-modal moves while the user interacts with it.
    useEffect(() => {
        if (!isVenueOpen) return;

        const html = document.documentElement;
        const body = document.body;
        const outerBody = modalBodyRef.current;

        const prev = {
            htmlOverflow: html.style.overflow,
            bodyOverflow: body.style.overflow,
            bodyPaddingRight: body.style.paddingRight,
            outerOverflow: outerBody?.style.overflow || "",
        };

        // Compensate for the scrollbar width so the layout doesn't jump when we
        // lock the body.
        const scrollbarWidth = window.innerWidth - html.clientWidth;
        if (scrollbarWidth > 0) body.style.paddingRight = `${scrollbarWidth}px`;

        html.style.overflow = "hidden";
        body.style.overflow = "hidden";
        if (outerBody) outerBody.style.overflow = "hidden";

        return () => {
            html.style.overflow = prev.htmlOverflow;
            body.style.overflow = prev.bodyOverflow;
            body.style.paddingRight = prev.bodyPaddingRight;
            if (outerBody) outerBody.style.overflow = prev.outerOverflow;
        };
    }, [isVenueOpen]);

    const { isLoaded: mapsLoaded } = useJsApiLoader({
        id: GOOGLE_MAPS_LOADER_ID,
        googleMapsApiKey: GOOGLE_MAPS_API_KEY,
        libraries: GOOGLE_LIBRARIES,
    });

    // ── Fetch active partner venues on open ────────────────────────────────────
    // (Interests are fetched inside <InterestPicker/>.)
    useEffect(() => {
        if (!isOpen) return;
        (async () => {
            setLoadingVenues(true);
            try {
                const q = query(collection(db, "venues"), where("active", "==", true));
                const snap = await getDocs(q);
                setPartnerVenues(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
            } catch (e) {
                console.error("Failed to load partner venues", e);
            } finally {
                setLoadingVenues(false);
            }
        })();
    }, [isOpen]);

    // ── Edit-mode prefill ─────────────────────────────────────────────────────
    // When an `event` prop is passed we hydrate the accordion with its data and
    // mark every section complete so the user can jump straight to any section.
    useEffect(() => {
        if (!isOpen) return;
        if (!isEditMode) return;

        const start = event.startTime?.toDate ? event.startTime.toDate() : event.startTime ? new Date(event.startTime) : null;
        const end = event.endTime?.toDate ? event.endTime.toDate() : event.endTime ? new Date(event.endTime) : null;

        setFormData({
            name: event.name || "",
            description: event.description || "",
            guestsToBring: event.guestsToBring || "",
            location: event.location || "",
            latitude: event.latitude ?? null,
            longitude: event.longitude ?? null,
            partnerVenueId: event.partnerVenueId || null,
            partnerVenueName: event.partnerVenueName || null,
            _partnerMinPricePerHour: Number(event.venuePricePerHour) || 0,
            startDate: start ? toDateInputString(start) : "",
            startTime: start ? toTimeInputString(start) : "",
            endDate: end ? toDateInputString(end) : "",
            endTime: end ? toTimeInputString(end) : "",
            cost: event.cost ?? 0,
            maxMembers: event.maxMembers ?? 10,
            interests: event.interests || [],
            imageUrl: event.imageUrl || "",
            online: event.online || false,
            showAdd: event.showAdd !== undefined ? event.showAdd : true,
        });
        setCompletedSections(SECTIONS.map(() => true));
        setOpenIndex(-1);
        setLocationTab(event.partnerVenueId ? 0 : 1);
        if (event.latitude && event.longitude) {
            setMapCenter({ lat: event.latitude, lng: event.longitude });
        }
    }, [isOpen, isEditMode, event]);

    // ── Create-mode draft hydration ────────────────────────────────────────────
    // Restore the user's in-progress `draftHangout/{uid}` doc so closing and
    // reopening the modal (or refreshing) picks up exactly where they left off.
    useEffect(() => {
        if (!isOpen || !user || isEditMode) return;

        let cancelled = false;
        setDraftLoading(true);
        loadEventDraft(user.uid)
            .then((draft) => {
                if (cancelled || !draft) return;
                const { formData: savedData, completedSections: savedSections } = draft;
                if (savedData && Object.keys(savedData).length > 0) {
                    setFormData({ ...DEFAULT_FORM_DATA, ...savedData });
                }
                const restored = SECTIONS.map((_, i) => savedSections?.[i] ?? false);
                setCompletedSections(restored);
                const firstIncomplete = restored.findIndex((c) => !c);
                setOpenIndex(firstIncomplete === -1 ? -1 : firstIncomplete);
                if (savedData?.partnerVenueId) setLocationTab(0);
                else if (savedData?.location) setLocationTab(1);
                if (savedData?.latitude && savedData?.longitude) {
                    setMapCenter({ lat: savedData.latitude, lng: savedData.longitude });
                }
            })
            .finally(() => {
                if (!cancelled) setDraftLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [isOpen, user, isEditMode]);

    // ── Auto-save draft (debounced 800ms, skip in edit mode) ──────────────────
    useEffect(() => {
        if (!isDirty || !user || isEditMode) return;
        const timer = setTimeout(() => {
            saveEventDraft(user.uid, formData, completedSections);
            setIsDirty(false);
        }, 800);
        return () => clearTimeout(timer);
    }, [formData, completedSections, isDirty, user, isEditMode]);

    // ── Field change ───────────────────────────────────────────────────────────
    const handleChange = useCallback((fieldId, value) => {
        setIsDirty(true);
        setFormData((prev) => {
            const next = { ...prev, [fieldId]: value };
            // If startDate moves forward past endDate, invalidate the stale endDate
            // so the user is forced to re-select a valid end date.
            if (fieldId === "startDate" && prev.endDate && value && prev.endDate < value) {
                next.endDate = "";
            }
            return next;
        });
    }, []);

    // ── Google Places selection ─────────────────────────────────────────────────
    const handlePlaceChanged = () => {
        const place = autocompleteRef.current?.getPlace();
        if (!place?.geometry) return;
        const location = place.formatted_address || place.name || "";
        const lat = place.geometry.location.lat();
        const lng = place.geometry.location.lng();
        setIsDirty(true);
        setFormData((prev) => ({
            ...prev,
            location,
            latitude: lat,
            longitude: lng,
            // Clear any partner venue selection when user picks a custom place
            partnerVenueId: null,
            partnerVenueName: null,
            _partnerMinPricePerHour: 0,
        }));
        setMapCenter({ lat, lng });
    };

    // ── Partner venue selection ────────────────────────────────────────────────
    const handleOpenVenuePreview = (venue) => {
        setPreviewVenue(venue);
        onVenueOpen();
    };

    const handleSelectPartnerVenue = (venue) => {
        const addr = [venue.streetAddress, venue.city, venue.province].filter(Boolean).join(", ");
        setIsDirty(true);
        setFormData((prev) => ({
            ...prev,
            partnerVenueId: venue.id,
            partnerVenueName: venue.venueName,
            // Stash the hourly rate on formData so the pure validator
            // (`requiredFieldsFilled`) can compute break-even feasibility.
            _partnerMinPricePerHour: Number(venue.minPricePerHour) || 0,
            location: addr || venue.venueName,
            latitude: venue.coordinates?.lat ?? null,
            longitude: venue.coordinates?.lng ?? null,
        }));
        if (venue.coordinates?.lat && venue.coordinates?.lng) {
            setMapCenter({ lat: venue.coordinates.lat, lng: venue.coordinates.lng });
        }
        onVenueClose();
        setPreviewVenue(null);
    };

    const handleClearPartnerVenue = () => {
        setIsDirty(true);
        setFormData((prev) => ({
            ...prev,
            partnerVenueId: null,
            partnerVenueName: null,
            _partnerMinPricePerHour: 0,
            location: "",
            latitude: null,
            longitude: null,
        }));
        setMapCenter(null);
    };

    // ── Image upload / remove (single) ─────────────────────────────────────────
    const handleImageUpload = async (files) => {
        if (!files || files.length === 0) return;
        const file = files[0];

        if (!file.type.startsWith("image/")) {
            toast({ title: "Invalid file type", description: "Please select an image file.", status: "error", duration: 3000 });
            return;
        }
        if (file.size > 5 * 1024 * 1024) {
            toast({ title: "File too large", description: "Image must be under 5MB.", status: "error", duration: 3000 });
            return;
        }

        setUploadingImage(true);
        try {
            const storage = getStorage();
            const path = `events/${Date.now()}_${file.name}`;
            const sref = ref(storage, path);
            await uploadBytes(sref, file);
            const url = await getDownloadURL(sref);
            setIsDirty(true);
            setFormData((prev) => ({ ...prev, imageUrl: url, _imagePath: path }));
        } catch (err) {
            toast({ title: "Upload failed", description: err.message, status: "error", duration: 4000 });
        } finally {
            setUploadingImage(false);
            if (fileInputRef.current) fileInputRef.current.value = "";
        }
    };

    const handleRemoveImage = async () => {
        const path = formData._imagePath;
        setIsDirty(true);
        setFormData((prev) => {
            const { imageUrl, _imagePath, ...rest } = prev;
            return rest;
        });
        if (path) {
            try {
                await deleteObject(ref(getStorage(), path));
            } catch (err) {
                console.warn("Could not delete image from storage:", err);
            }
        }
    };

    // ── Interest picker → DocumentReference[] ─────────────────────────────────
    // The picker speaks in ids; the payload stores DocumentReferences so
    // downstream code (event reads, queries) keeps working unchanged.
    const selectedInterestIds = (formData.interests || []).map(
        (r) => r?.id || (typeof r?.path === "string" ? r.path.split("/").pop() : null)
    ).filter(Boolean);

    const handleInterestsChange = (nextIds) => {
        setIsDirty(true);
        setFormData((prev) => ({
            ...prev,
            interests: nextIds.map((id) => doc(db, "interests", id)),
        }));
    };

    // ── Continue (complete section) ─────────────────────────────────────────────
    const handleContinue = (sIdx) => {
        const section = SECTIONS[sIdx];
        if (!requiredFieldsFilled(section, formData)) {
            toast({
                title: "Fill in all required fields",
                description: `Please complete all required fields in "${section.title}" before continuing.`,
                status: "warning",
                duration: 3000,
                isClosable: true,
            });
            return;
        }
        const updated = completedSections.map((v, i) => (i === sIdx ? true : v));
        setCompletedSections(updated);
        setIsDirty(false); // cancel any pending debounced save before firing directly
        const next = updated.findIndex((c) => !c);
        setOpenIndex(next === -1 ? -1 : next);
        // Only persist as a draft for brand-new events. Edits shouldn't pollute
        // the user's draftHangout doc (would "revive" an existing event as a
        // new draft the next time the modal opens for a fresh event).
        if (!isEditMode && user) {
            saveEventDraft(user.uid, formData, updated);
        }
    };

    // ── Submit ──────────────────────────────────────────────────────────────────
    const handleSubmit = async () => {
        // Cancel any pending debounced draft save before we submit + delete the
        // draft, otherwise a stale timer could re-create the doc right after.
        setIsDirty(false);
        setSubmitting(true);
        try {
            if (isEditMode) {
                // Update the existing document — host/createdAt/members are untouched.
                const sharedFields = buildSharedEventFields(formData, partnerVenues);
                // Belt-and-suspenders: the UI already disables these inputs
                // for approved/confirmed events, but strip them from the
                // payload too so a tampered DOM can't change anything that
                // guests or the venue have already committed to.
                if (lockApprovedFields) {
                    delete sharedFields.location;
                    delete sharedFields.latitude;
                    delete sharedFields.longitude;
                    delete sharedFields.partnerVenueId;
                    delete sharedFields.partnerVenueName;
                    delete sharedFields.venuePricePerHour;
                    delete sharedFields.venueBookingHours;
                    delete sharedFields.venueMinimumRevenue;
                    // startTime/endTime stay in the payload — hosts can move
                    // the date of an approved event. confirmationDeadline is
                    // derived from startTime, so it rides along rather than
                    // being left pointing at the old date.
                }
                if (lockPricingFields) {
                    delete sharedFields.cost;
                    delete sharedFields.maxMembers;
                }
                const docId = event.docId || event.id;
                await updateDoc(doc(db, "events", docId), sharedFields);
                toast({
                    title: "Event updated!",
                    description: "Your changes have been saved.",
                    status: "success",
                    duration: 2500,
                    isClosable: true,
                });
                if (onEventUpdated) onEventUpdated();
                handleClose();
            } else {
                const eventId = generateEventId();
                const eventData = buildCreateEventPayload({ formData, partnerVenues, user, eventId });
                await setDoc(doc(db, "events", eventId), eventData);
                await deleteEventDraft(user.uid);
                toast({
                    title: "Event created!",
                    description: "Redirecting…",
                    status: "success",
                    duration: 2000,
                    isClosable: true,
                });

                handleClose();
                setTimeout(() => navigate(`/hangout/${toUrlSafeEventId(eventId)}`), 400);
            }
        } catch (err) {
            console.error(err);
            toast({
                title: isEditMode ? "Failed to update event" : "Failed to create event",
                description: err.message,
                status: "error",
                duration: 5000,
                isClosable: true,
            });
        } finally {
            setSubmitting(false);
        }
    };

    // ── Close / reset ───────────────────────────────────────────────────────────
    const handleClose = () => {
        setIsDirty(false);
        setFormData(DEFAULT_FORM_DATA);
        setCompletedSections(SECTIONS.map(() => false));
        setOpenIndex(0);
        setMapCenter(null);
        setLocationTab(0);
        setPreviewVenue(null);
        onClose();
    };

    const allComplete = completedSections.every(Boolean);

    // Tiny inline "Locked" badge re-used by every field that's frozen
    // because the event has already been approved/confirmed. The reason
    // ("approved" or "confirmed") flows into the tooltip copy so the host
    // knows exactly why they can't edit. The dark, illustrated tooltip
    // mirrors the one EventsPage uses for the venue owner's locked
    // approve/reject buttons so the language and look feel native.
    const renderLockedPill = (reason) => {
        const title =
            reason === "confirmed" ? "Confirmed & locked" : "Approved & locked";
        const tooltipLabel = (
            <HStack spacing={2} align="flex-start" minW="220px">
                <Flex
                    w="26px"
                    h="26px"
                    borderRadius="md"
                    bg="whiteAlpha.200"
                    align="center"
                    justify="center"
                    flexShrink={0}
                >
                    <Icon as={IoLockClosedOutline} color="white" boxSize="13px" />
                </Flex>
                <VStack align="flex-start" spacing="2px">
                    <Text fontSize="12px" fontWeight="700" color="white" lineHeight="1.2">
                        {title}
                    </Text>
                    <HStack spacing={1} align="center">
                        <Icon as={IoMailOutline} color="whiteAlpha.700" boxSize="11px" />
                        <Text fontSize="11px" color="whiteAlpha.800" lineHeight="1.3">
                            arpan@proximy.ca
                        </Text>
                    </HStack>
                    <Text fontSize="10px" color="whiteAlpha.600" lineHeight="1.3">
                        Reach out for any changes.
                    </Text>
                </VStack>
            </HStack>
        );

        return (
            <Tooltip
                label={tooltipLabel}
                hasArrow
                placement="top"
                bg="gray.900"
                color="white"
                borderRadius="lg"
                px={3}
                py={2}
                boxShadow="0 14px 30px -10px rgba(15, 23, 42, 0.6)"
                openDelay={120}
                shouldWrapChildren
            >
                <Box
                    as="span"
                    display="inline-flex"
                    alignItems="center"
                    bg="gray.100"
                    color="gray.500"
                    borderRadius="full"
                    px={1.5}
                    py="1px"
                    cursor="help"
                    gap="3px"
                >
                    <Icon as={IoLockClosedOutline} boxSize="9px" />
                    <Text as="span" fontSize="9px" fontWeight="700" textTransform="uppercase" letterSpacing="0.04em">
                        Locked
                    </Text>
                </Box>
            </Tooltip>
        );
    };

    // ── Field renderer ──────────────────────────────────────────────────────────
    const renderField = (field) => {
        // ── Single image uploader ────────────────────────────────────────────────
        if (field.type === "image") {
            const hasImage = !!formData.imageUrl;
            return (
                <FormControl key={field.id} isRequired={field.required}>
                    <FormLabel fontSize="xs" fontWeight="semibold" color="gray.600" mb={2}>
                        {field.label}
                    </FormLabel>

                    {hasImage ? (
                        <Box position="relative" borderRadius="xl" overflow="hidden" border="1px solid" borderColor="gray.200">
                            <Image src={formData.imageUrl} alt="Event cover" w="full" h="200px" objectFit="cover" />
                            <IconButton
                                icon={<IoClose />}
                                aria-label="Remove image"
                                size="sm"
                                position="absolute"
                                top={2}
                                right={2}
                                bg="blackAlpha.700"
                                color="white"
                                borderRadius="full"
                                _hover={{ bg: "red.500" }}
                                onClick={handleRemoveImage}
                            />
                        </Box>
                    ) : (
                        <Box
                            as="button"
                            type="button"
                            onClick={() => fileInputRef.current?.click()}
                            w="full"
                            h="160px"
                            borderRadius="xl"
                            border="1.5px dashed"
                            borderColor={uploadingImage ? "#508cf7" : "gray.300"}
                            bg={uploadingImage ? "blue.50" : "gray.50"}
                            _hover={{ bg: "blue.50", borderColor: "#508cf7" }}
                            display="flex"
                            flexDirection="column"
                            alignItems="center"
                            justifyContent="center"
                            transition="all 0.15s"
                            disabled={uploadingImage}
                        >
                            {uploadingImage ? (
                                <Spinner size="md" color="#508cf7" />
                            ) : (
                                <>
                                    <Icon as={IoCloudUploadOutline} boxSize={8} color="#508cf7" />
                                    <Text fontSize="sm" fontWeight="medium" color="gray.700" mt={2}>
                                        Click to upload
                                    </Text>
                                    <Text fontSize="11px" color="gray.400">Max 5MB • JPG, PNG, GIF</Text>
                                </>
                            )}
                        </Box>
                    )}

                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        style={{ display: "none" }}
                        onChange={(e) => handleImageUpload(e.target.files)}
                    />
                </FormControl>
            );
        }

        // ── Address search ───────────────────────────────────────────────────────
        if (field.type === "address-search") {
            const selectedPartner = partnerVenues.find((v) => v.id === formData.partnerVenueId);

            // Approved events freeze location entirely — guests already
            // know where to show up. Render a read-only summary in place
            // of the partner-venue / custom-place tab UI.
            if (lockApprovedFields) {
                const lat = formData.latitude;
                const lng = formData.longitude;
                return (
                    <FormControl key={field.id} isReadOnly>
                        <FormLabel
                            fontSize="xs"
                            fontWeight="semibold"
                            color="gray.600"
                            mb={1}
                            display="inline-flex"
                            alignItems="center"
                            flexWrap="wrap"
                            gap="6px"
                        >
                            <Text as="span">Location</Text>
                            {renderLockedPill("approved")}
                        </FormLabel>
                        <Box
                            bg="gray.50"
                            border="1px solid"
                            borderColor="gray.200"
                            borderRadius="xl"
                            p={3}
                        >
                            <HStack spacing={3} align="center">
                                <Flex
                                    boxSize="44px"
                                    bg="white"
                                    border="1px solid"
                                    borderColor="gray.200"
                                    borderRadius="lg"
                                    align="center"
                                    justify="center"
                                    flexShrink={0}
                                >
                                    <Icon
                                        as={selectedPartner ? IoStorefrontOutline : IoLocationOutline}
                                        color="gray.500"
                                        boxSize={5}
                                    />
                                </Flex>
                                <Box flex="1" minW={0} textAlign="left">
                                    {selectedPartner && (
                                        <Text fontSize="sm" fontWeight="600" color="gray.800" noOfLines={1}>
                                            {selectedPartner.venueName}
                                        </Text>
                                    )}
                                    <Text fontSize="11px" color="gray.500" noOfLines={2}>
                                        {formData.location || "—"}
                                    </Text>
                                </Box>
                            </HStack>
                            {mapsLoaded && lat != null && lng != null && (
                                <Box
                                    mt={3}
                                    borderRadius="lg"
                                    overflow="hidden"
                                    border="1px solid"
                                    borderColor="gray.200"
                                >
                                    <GoogleMap
                                        mapContainerStyle={MAP_CONTAINER_STYLE}
                                        center={{ lat, lng }}
                                        zoom={15}
                                        options={MAP_OPTIONS}
                                    >
                                        <MarkerF position={{ lat, lng }} />
                                    </GoogleMap>
                                </Box>
                            )}
                        </Box>
                    </FormControl>
                );
            }

            return (
                <FormControl key={field.id} isRequired={field.required}>
                    <Tabs
                        index={locationTab}
                        onChange={setLocationTab}
                        variant="soft-rounded"
                        colorScheme="blue"
                        size="sm"
                    >
                        <TabList bg="gray.100" p={1} borderRadius="lg" mb={4}>
                            <Tab
                                flex="1"
                                fontSize="xs"
                                fontWeight="semibold"
                                _selected={{ bg: "white", color: "#508cf7", boxShadow: "sm" }}
                                color="gray.500"
                                borderRadius="md"
                            >
                                <Icon as={IoStorefrontOutline} boxSize={3.5} mr={1.5} />
                                Partner Venue
                            </Tab>
                            <Tab
                                flex="1"
                                fontSize="xs"
                                fontWeight="semibold"
                                _selected={{ bg: "white", color: "#508cf7", boxShadow: "sm" }}
                                color="gray.500"
                                borderRadius="md"
                            >
                                <Icon as={IoLocationOutline} boxSize={3.5} mr={1.5} />
                                Custom Place
                            </Tab>
                        </TabList>

                        <TabPanels>
                            {/* ── Partner Venue tab ─────────────────────────────────────── */}
                            <TabPanel p={0}>
                                {loadingVenues ? (
                                    <VStack spacing={2}>
                                        {[0, 1].map((i) => <Skeleton key={i} height="72px" borderRadius="xl" />)}
                                    </VStack>
                                ) : partnerVenues.length === 0 ? (
                                    <Box
                                        py={8}
                                        textAlign="center"
                                        border="1px dashed"
                                        borderColor="gray.200"
                                        borderRadius="xl"
                                    >
                                        <Icon as={IoStorefrontOutline} boxSize={8} color="gray.300" mb={2} />
                                        <Text fontSize="sm" color="gray.500" fontWeight="medium">No partner venues yet</Text>
                                        <Text fontSize="11px" color="gray.400">Switch to Custom Place to pick any location.</Text>
                                    </Box>
                                ) : (
                                    <VStack spacing={2} align="stretch">
                                        {selectedPartner && (
                                            <>
                                                <Flex
                                                    p={3}
                                                    bg="blue.50"
                                                    border="1.5px solid"
                                                    borderColor="#508cf7"
                                                    borderRadius="xl"
                                                    align="center"
                                                    gap={3}
                                                >
                                                    <Icon as={IoCheckmarkCircle} color="#508cf7" boxSize={5} flexShrink={0} />
                                                    <Box flex="1" minW={0}>
                                                        <Text fontSize="sm" fontWeight="600" color="gray.800" noOfLines={1}>
                                                            {selectedPartner.venueName}
                                                        </Text>
                                                        <Text fontSize="11px" color="gray.500" noOfLines={1}>
                                                            {[selectedPartner.city, selectedPartner.province].filter(Boolean).join(", ")}
                                                        </Text>
                                                    </Box>
                                                    <IconButton
                                                        icon={<IoClose />}
                                                        aria-label="Clear selection"
                                                        size="xs"
                                                        variant="ghost"
                                                        color="gray.500"
                                                        onClick={handleClearPartnerVenue}
                                                    />
                                                </Flex>

                                                {/* Address preview map for the selected partner venue */}
                                                {mapsLoaded && selectedPartner.coordinates?.lat && selectedPartner.coordinates?.lng && (
                                                    <Box mt={1} borderRadius="xl" overflow="hidden" border="1px solid" borderColor="gray.200">
                                                        <GoogleMap
                                                            mapContainerStyle={MAP_CONTAINER_STYLE}
                                                            center={{
                                                                lat: selectedPartner.coordinates.lat,
                                                                lng: selectedPartner.coordinates.lng,
                                                            }}
                                                            zoom={15}
                                                            options={MAP_OPTIONS}
                                                        >
                                                            <MarkerF
                                                                position={{
                                                                    lat: selectedPartner.coordinates.lat,
                                                                    lng: selectedPartner.coordinates.lng,
                                                                }}
                                                            />
                                                        </GoogleMap>
                                                    </Box>
                                                )}
                                                {formData.location && (
                                                    <HStack spacing={1.5} fontSize="11px" color="gray.600" mt={1}>
                                                        <Icon as={IoLocationOutline} boxSize="12px" color="gray.400" />
                                                        <Text noOfLines={1}>{formData.location}</Text>
                                                    </HStack>
                                                )}
                                            </>
                                        )}

                                        {partnerVenues
                                            .filter((v) => v.id !== formData.partnerVenueId)
                                            .map((v) => (
                                                <Flex
                                                    key={v.id}
                                                    as="button"
                                                    type="button"
                                                    onClick={() => handleOpenVenuePreview(v)}
                                                    p={2}
                                                    bg="white"
                                                    border="1px solid"
                                                    borderColor="gray.200"
                                                    borderRadius="xl"
                                                    align="center"
                                                    gap={3}
                                                    _hover={{ borderColor: "#508cf7", bg: "blue.50" }}
                                                    transition="all 0.15s"
                                                    textAlign="left"
                                                >
                                                    {v.images?.[0]?.url ? (
                                                        <Image
                                                            src={v.images[0].url}
                                                            alt={v.venueName}
                                                            boxSize="52px"
                                                            objectFit="cover"
                                                            borderRadius="lg"
                                                            flexShrink={0}
                                                        />
                                                    ) : (
                                                        <Flex
                                                            boxSize="52px"
                                                            bg="gray.100"
                                                            borderRadius="lg"
                                                            align="center"
                                                            justify="center"
                                                            flexShrink={0}
                                                        >
                                                            <Icon as={IoImagesOutline} color="gray.400" boxSize={5} />
                                                        </Flex>
                                                    )}
                                                    <Box flex="1" minW={0}>
                                                        <Text fontSize="sm" fontWeight="600" color="gray.800" noOfLines={1}>
                                                            {v.venueName}
                                                        </Text>
                                                        <HStack spacing={1} fontSize="11px" color="gray.500">
                                                            <Icon as={IoLocationOutline} boxSize="11px" />
                                                            <Text noOfLines={1}>
                                                                {[v.city, v.province].filter(Boolean).join(", ") || "—"}
                                                            </Text>
                                                        </HStack>
                                                    </Box>
                                                    <Text fontSize="11px" color="#508cf7" fontWeight="600" flexShrink={0} pr={2}>
                                                        View
                                                    </Text>
                                                </Flex>
                                            ))}
                                    </VStack>
                                )}
                            </TabPanel>

                            {/* ── Custom Place tab ─────────────────────────────────────── */}
                            <TabPanel p={0}>
                                {mapsLoaded ? (
                                    <>
                                        <Autocomplete
                                            onLoad={(ref) => { autocompleteRef.current = ref; }}
                                            onPlaceChanged={handlePlaceChanged}
                                        >
                                            <Input
                                                size="sm"
                                                borderRadius="lg"
                                                placeholder={field.placeholder}
                                                focusBorderColor="#508cf7"
                                                bg="blue.50"
                                                defaultValue={formData.location ?? ""}
                                            />
                                        </Autocomplete>
                                        {mapCenter && (
                                            <Box mt={3} borderRadius="xl" overflow="hidden" border="1px solid" borderColor="gray.200">
                                                <GoogleMap
                                                    mapContainerStyle={MAP_CONTAINER_STYLE}
                                                    center={mapCenter}
                                                    zoom={15}
                                                    options={MAP_OPTIONS}
                                                >
                                                    <MarkerF position={mapCenter} />
                                                </GoogleMap>
                                            </Box>
                                        )}
                                        {formData.location && !formData.partnerVenueId && (
                                            <Text fontSize="11px" color="gray.500" mt={2} noOfLines={1}>
                                                {formData.location}
                                            </Text>
                                        )}
                                    </>
                                ) : (
                                    <Skeleton height="32px" borderRadius="lg" />
                                )}
                            </TabPanel>
                        </TabPanels>
                    </Tabs>
                </FormControl>
            );
        }

        // ── Interests chip grid ──────────────────────────────────────────────────
        if (field.type === "interests") {
            return (
                <FormControl key={field.id} isRequired={field.required}>
                    <FormLabel fontSize="xs" fontWeight="semibold" color="gray.600" mb={2}>
                        {field.label}
                        <Text as="span" fontSize="10px" color="gray.400" ml={2}>
                            {selectedInterestIds.length > 0
                                ? `${selectedInterestIds.length} selected`
                                : "pick at least 1"}
                        </Text>
                    </FormLabel>
                    <InterestPicker
                        selectedIds={selectedInterestIds}
                        onChange={handleInterestsChange}
                    />
                </FormControl>
            );
        }

        // End time becomes required once a partner venue is selected.
        const isEndTime = field.id === "endTime";
        const isEndDate = field.id === "endDate";
        const isPartnerCost = field.id === "cost" && !!formData.partnerVenueId;
        const effectiveRequired =
            field.required || (isEndTime && !!formData.partnerVenueId) || isPartnerCost;

        // Confirmed events freeze cost + max attendees — those are what
        // guests actually committed to. Scheduling stays editable even
        // after approval so hosts can move an event's date themselves.
        const isPricingField = field.id === "cost" || field.id === "maxMembers";
        const isLockedField = lockPricingFields && isPricingField;

        // Date / number input constraints
        const extraProps = {};
        if (isEndDate && formData.startDate) extraProps.min = formData.startDate;
        if (isPartnerCost) {
            extraProps.min = 1;
            extraProps.step = "1";
        }

        // ── Default text/number/textarea/date/time ──────────────────────────────
        return (
            <FormControl key={field.id} isRequired={effectiveRequired} isReadOnly={isLockedField}>
                <FormLabel
                    fontSize="xs"
                    fontWeight="semibold"
                    color="gray.600"
                    mb={1}
                    display="inline-flex"
                    alignItems="center"
                    flexWrap="wrap"
                    gap="6px"
                >
                    <Text as="span">{field.label}</Text>
                    {isLockedField && renderLockedPill("confirmed")}
                    {isEndTime && formData.partnerVenueId && !isLockedField && (
                        <Text as="span" fontSize="10px" color="#508cf7" fontWeight="500">
                            required for partner venues
                        </Text>
                    )}
                    {isPartnerCost && !isLockedField && (
                        <Text as="span" fontSize="10px" color="#508cf7" fontWeight="500">
                            must be greater than $0
                        </Text>
                    )}
                </FormLabel>
                {field.type === "textarea" ? (
                    field.id === "description" ? (
                        // WYSIWYG editor — what you type is what your
                        // guests see on the event page. No syntax, no
                        // modes, just formatted text.
                        <RichTextEditor
                            value={formData[field.id] ?? ""}
                            onChange={(html) => handleChange(field.id, html)}
                            placeholder={field.placeholder}
                        />
                    ) : (
                        <Textarea
                            size="sm"
                            borderRadius="lg"
                            placeholder={field.placeholder}
                            value={formData[field.id] ?? ""}
                            onChange={(e) => handleChange(field.id, e.target.value)}
                            focusBorderColor="#508cf7"
                            rows={3}
                        />
                    )
                ) : (
                    <Input
                        size="sm"
                        borderRadius="lg"
                        type={field.type}
                        placeholder={field.placeholder}
                        value={formData[field.id] ?? ""}
                        onChange={(e) => handleChange(field.id, e.target.value)}
                        focusBorderColor="#508cf7"
                        isDisabled={isLockedField}
                        bg={isLockedField ? "gray.50" : undefined}
                        cursor={isLockedField ? "not-allowed" : undefined}
                        {...extraProps}
                    />
                )}
            </FormControl>
        );
    };

    // ── Render ──────────────────────────────────────────────────────────────────
    return (
        <>
            <Modal
                isOpen={isOpen}
                onClose={handleClose}
                isCentered
                size={{ base: "full", md: "lg" }}
                scrollBehavior="inside"
                // When the partner-venue sub-modal is open we disable Chakra's
                // react-remove-scroll wrapper. Otherwise it intercepts wheel/touch
                // events across the entire document and our custom sub-modal can't
                // scroll its own content.
                blockScrollOnMount={!isVenueOpen}
                trapFocus={!isVenueOpen}
            >
                <ModalOverlay backdropFilter="blur(4px)" bg="blackAlpha.500" />
                <ModalContent borderRadius={{ base: 0, md: "2xl" }} overflow="hidden">
                    <ModalHeader bg="#508cf7" color="white" fontWeight="bold" fontSize="lg" pb={4} pt={5}>
                        <HStack spacing={3}>
                            <Icon as={IoCalendarOutline} boxSize={5} />
                            <Text>{isEditMode ? "Edit Event" : "New Event"}</Text>
                        </HStack>
                    </ModalHeader>
                    <ModalCloseButton color="white" top={4} />

                    <ModalBody ref={modalBodyRef} py={6} px={5}>
                        <VStack spacing={4} align="stretch">
                            <Text fontSize="sm" color="gray.500">
                                {isEditMode
                                    ? "Tap any section to update its details."
                                    : "Complete each section to publish your event."}
                            </Text>

                            <Accordion allowToggle index={openIndex} onChange={(i) => setOpenIndex(i)}>
                                {SECTIONS.map((section, sIdx) => {
                                    const done = completedSections[sIdx];
                                    const canContinue = requiredFieldsFilled(section, formData);
                                    const isExpanded = openIndex === sIdx;

                                    return (
                                        <AccordionItem
                                            key={section.id}
                                            border="1px solid"
                                            borderColor={done ? "green.200" : isExpanded ? "#508cf7" : "gray.200"}
                                            borderRadius="xl"
                                            mb={3}
                                            overflow="hidden"
                                        >
                                            <AccordionButton
                                                py={3}
                                                px={4}
                                                bg={done ? "green.50" : "white"}
                                                _hover={{ bg: done ? "green.50" : "gray.50" }}
                                                _expanded={{ bg: "white", borderBottom: "1px solid", borderColor: "gray.100" }}
                                            >
                                                <HStack flex="1" spacing={3}>
                                                    {done ? (
                                                        <Icon as={IoCheckmarkCircle} color="green.500" boxSize={5} flexShrink={0} />
                                                    ) : (
                                                        <Box
                                                            w="20px" h="20px"
                                                            borderRadius="full"
                                                            border="2px solid"
                                                            borderColor={isExpanded ? "#508cf7" : "gray.300"}
                                                            display="flex" alignItems="center" justifyContent="center"
                                                            flexShrink={0}
                                                        >
                                                            <Text fontSize="10px" fontWeight="bold" color={isExpanded ? "#508cf7" : "gray.400"}>
                                                                {sIdx + 1}
                                                            </Text>
                                                        </Box>
                                                    )}
                                                    <Text fontWeight="semibold" fontSize="sm" color={done ? "green.700" : "gray.800"}>
                                                        {section.title}
                                                    </Text>
                                                    {done && (
                                                        <Badge colorScheme="green" fontSize="9px" borderRadius="full" px={2}>
                                                            Complete
                                                        </Badge>
                                                    )}
                                                </HStack>
                                            </AccordionButton>

                                            <AccordionPanel pb={4} px={4} pt={4} bg="white">
                                                <VStack spacing={4} align="stretch">
                                                    {section.fields.map((field) => renderField(field))}

                                                    {/* Live break-even callout when a partner venue is selected:
                            price × tickets = minimum revenue to confirm. */}
                                                    {section.id === "pricing" && formData.partnerVenueId && (() => {
                                                        const partner = partnerVenues.find((v) => v.id === formData.partnerVenueId);
                                                        const pricePerHour = Number(partner?.minPricePerHour) || 0;
                                                        const hours = computeEventHours(formData);
                                                        const minRevenue = hours * pricePerHour;
                                                        const ticketPrice = Number(formData.cost) || 0;
                                                        const capacity = Number(formData.maxMembers) || 0;
                                                        const ticketsNeeded = ticketPrice > 0 ? Math.ceil(minRevenue / ticketPrice) : 0;
                                                        const feasible = ticketPrice > 0 && ticketsNeeded > 0 && (capacity === 0 || ticketsNeeded <= capacity);
                                                        const capacityOverflow = capacity > 0 && ticketsNeeded > capacity;
                                                        return (
                                                            <Box
                                                                bg={feasible ? "blue.50" : capacityOverflow ? "red.50" : "orange.50"}
                                                                border="1px solid"
                                                                borderColor={feasible ? "blue.100" : capacityOverflow ? "red.100" : "orange.100"}
                                                                borderRadius="lg"
                                                                px={3}
                                                                py={2.5}
                                                            >
                                                                <HStack spacing={2} align="flex-start">
                                                                    <Icon
                                                                        as={capacityOverflow ? IoWarningOutline : IoCashOutline}
                                                                        color={feasible ? "#508cf7" : capacityOverflow ? "red.500" : "orange.500"}
                                                                        boxSize={4}
                                                                        mt="2px"
                                                                    />
                                                                    <Box flex="1">
                                                                        <Text
                                                                            fontSize="10px"
                                                                            fontWeight="700"
                                                                            color={feasible ? "#508cf7" : capacityOverflow ? "red.600" : "orange.600"}
                                                                            textTransform="uppercase"
                                                                            letterSpacing="0.05em"
                                                                        >
                                                                            Break-even
                                                                        </Text>
                                                                        {minRevenue <= 0 ? (
                                                                            <Text fontSize="xs" color="gray.500">
                                                                                Set a start + end time in "Date & Time" to calculate your minimum.
                                                                            </Text>
                                                                        ) : ticketPrice <= 0 ? (
                                                                            <Text fontSize="xs" color="gray.600">
                                                                                Partner venues need a positive ticket price so you can hit the{" "}
                                                                                <Text as="span" fontWeight="700" color="gray.800">
                                                                                    {formatMoney(minRevenue)}
                                                                                </Text>{" "}
                                                                                minimum.
                                                                            </Text>
                                                                        ) : (
                                                                            <Text fontSize="xs" color="gray.600">
                                                                                At{" "}
                                                                                <Text as="span" fontWeight="700" color="gray.800">
                                                                                    {formatMoney(ticketPrice)}
                                                                                </Text>
                                                                                /ticket you need to sell{" "}
                                                                                <Text as="span" fontWeight="700" color="gray.800">
                                                                                    {ticketsNeeded}
                                                                                </Text>{" "}
                                                                                {ticketsNeeded === 1 ? "ticket" : "tickets"} to cover{" "}
                                                                                <Text as="span" fontWeight="700" color="gray.800">
                                                                                    {formatMoney(minRevenue)}
                                                                                </Text>
                                                                                .
                                                                                {capacityOverflow && (
                                                                                    <Text as="span" color="red.600" fontWeight="600">
                                                                                        {" "}— exceeds your {capacity}-seat capacity, raise price or capacity.
                                                                                    </Text>
                                                                                )}
                                                                            </Text>
                                                                        )}
                                                                    </Box>
                                                                </HStack>
                                                            </Box>
                                                        );
                                                    })()}

                                                    {/* Live minimum-revenue callout when a partner venue is selected */}
                                                    {section.id === "schedule" && formData.partnerVenueId && (() => {
                                                        const partner = partnerVenues.find((v) => v.id === formData.partnerVenueId);
                                                        const pricePerHour = Number(partner?.minPricePerHour) || 0;
                                                        const hours = computeEventHours(formData);
                                                        const min = hours * pricePerHour;
                                                        return (
                                                            <Box
                                                                bg="blue.50"
                                                                border="1px solid"
                                                                borderColor="blue.100"
                                                                borderRadius="lg"
                                                                px={3}
                                                                py={2.5}
                                                            >
                                                                <HStack spacing={2} align="center">
                                                                    <Icon as={IoCashOutline} color="#508cf7" boxSize={4} />
                                                                    <Box flex="1">
                                                                        <Text fontSize="10px" fontWeight="700" color="#508cf7" textTransform="uppercase" letterSpacing="0.05em">
                                                                            Minimum to confirm
                                                                        </Text>
                                                                        {hours > 0 ? (
                                                                            <Text fontSize="xs" color="gray.600">
                                                                                {hours}h × {formatMoney(pricePerHour)} ={" "}
                                                                                <Text as="span" fontWeight="700" color="gray.800">
                                                                                    {formatMoney(min)}
                                                                                </Text>
                                                                                <Text as="span" color="gray.400"> • 5h before start</Text>
                                                                            </Text>
                                                                        ) : (
                                                                            <Text fontSize="xs" color="gray.500">
                                                                                Set start + end time to see your minimum.
                                                                            </Text>
                                                                        )}
                                                                    </Box>
                                                                </HStack>
                                                            </Box>
                                                        );
                                                    })()}

                                                    <Button
                                                        size="sm"
                                                        bg={canContinue ? "#508cf7" : "gray.100"}
                                                        color={canContinue ? "white" : "gray.400"}
                                                        _hover={{ bg: canContinue ? "#226cf1" : "gray.100" }}
                                                        borderRadius="lg"
                                                        alignSelf="flex-end"
                                                        onClick={() => handleContinue(sIdx)}
                                                        cursor={canContinue ? "pointer" : "not-allowed"}
                                                        mt={1}
                                                    >
                                                        {done ? "Update & Continue" : "Continue"}
                                                    </Button>
                                                </VStack>
                                            </AccordionPanel>
                                        </AccordionItem>
                                    );
                                })}
                            </Accordion>

                            <Button
                                bg="#508cf7"
                                color="white"
                                _hover={{ bg: "#226cf1" }}
                                isDisabled={!allComplete || submitting}
                                onClick={handleSubmit}
                                borderRadius="xl"
                                size="md"
                                w="full"
                                mt={1}
                            >
                                {submitting ? <Spinner size="sm" /> : isEditMode ? "Save Changes" : "Create Event"}
                            </Button>
                        </VStack>
                    </ModalBody>
                </ModalContent>
            </Modal>

            {/* ── Partner venue preview sub-modal (fully custom overlay) ────────
        Rendered as plain <div>s portalled to <body> with inline styles —
        zero Chakra layout primitives in the outer shell — so it's
        completely immune to the outer modal's scroll-lock / focus-trap. */}
            {isVenueOpen && previewVenue && createPortal(
                <div
                    onClick={onVenueClose}
                    style={{
                        position: "fixed",
                        inset: 0,
                        zIndex: 9999,
                        backgroundColor: "rgba(0, 0, 0, 0.6)",
                        backdropFilter: "blur(4px)",
                        WebkitBackdropFilter: "blur(4px)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: "1rem",
                        overscrollBehavior: "contain",
                    }}
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        onWheel={(e) => e.stopPropagation()}
                        onTouchMove={(e) => e.stopPropagation()}
                        style={{
                            backgroundColor: "white",
                            borderRadius: "16px",
                            overflow: "hidden",
                            maxWidth: "480px",
                            width: "100%",
                            maxHeight: "90vh",
                            display: "flex",
                            flexDirection: "column",
                            boxShadow: "0 25px 50px -12px rgba(0,0,0,0.4)",
                            position: "relative",
                        }}
                    >
                        {/* Hero gallery */}
                        <VenuePreviewGallery
                            images={previewVenue.images}
                            venueName={previewVenue.venueName}
                        />

                        <button
                            type="button"
                            aria-label="Close"
                            onClick={onVenueClose}
                            style={{
                                position: "absolute",
                                top: 12,
                                right: 12,
                                width: 32,
                                height: 32,
                                borderRadius: "50%",
                                background: "rgba(0,0,0,0.5)",
                                color: "white",
                                border: "none",
                                cursor: "pointer",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                zIndex: 2,
                            }}
                        >
                            <IoClose size={16} />
                        </button>

                        {/* Scrollable body — plain div so nothing overrides overflow */}
                        <div
                            style={{
                                flex: "1 1 auto",
                                minHeight: 0,
                                overflowY: "auto",
                                WebkitOverflowScrolling: "touch",
                                padding: "20px",
                            }}
                        >
                            <VStack spacing={4} align="stretch">
                                {/* Type tags */}
                                {(previewVenue.venueType || previewVenue.primaryEventType) && (
                                    <Flex gap={2} flexWrap="wrap">
                                        {previewVenue.venueType && (
                                            <HStack
                                                spacing={1.5}
                                                bg="blue.50"
                                                color="#226cf1"
                                                borderRadius="full"
                                                px={3}
                                                py={1}
                                                border="1px solid"
                                                borderColor="blue.100"
                                            >
                                                <Icon as={IoStorefrontOutline} boxSize={3.5} />
                                                <Text fontSize="11px" fontWeight="700">
                                                    {previewVenue.venueType}
                                                </Text>
                                            </HStack>
                                        )}
                                        {previewVenue.primaryEventType && (
                                            <HStack
                                                spacing={1.5}
                                                bg="purple.50"
                                                color="purple.600"
                                                borderRadius="full"
                                                px={3}
                                                py={1}
                                                border="1px solid"
                                                borderColor="purple.100"
                                            >
                                                <Icon as={IoSparklesOutline} boxSize={3.5} />
                                                <Text fontSize="11px" fontWeight="700">
                                                    {previewVenue.primaryEventType}
                                                </Text>
                                            </HStack>
                                        )}
                                    </Flex>
                                )}

                                {previewVenue.description && (
                                    <Text fontSize="sm" color="gray.600" lineHeight="1.6">
                                        {previewVenue.description}
                                    </Text>
                                )}

                                {/* Address */}
                                {(previewVenue.streetAddress || previewVenue.city) && (
                                    <HStack spacing={2} color="gray.500">
                                        <Icon as={IoLocationOutline} boxSize={4} color="gray.400" />
                                        <Text fontSize="13px">
                                            {[previewVenue.streetAddress, previewVenue.city, previewVenue.province]
                                                .filter(Boolean)
                                                .join(", ")}
                                        </Text>
                                    </HStack>
                                )}

                                {/* Stat strip */}
                                <HStack
                                    spacing={0}
                                    bg="gray.50"
                                    borderRadius="xl"
                                    overflow="hidden"
                                    divider={<Box w="1px" h="full" bg="gray.100" flexShrink={0} />}
                                >
                                    {[
                                        { icon: IoPeopleOutline, label: "Capacity", value: previewVenue.maxCapacity ?? "—" },
                                        { icon: IoResizeOutline, label: "Sq Ft", value: previewVenue.squareFootage ?? "—" },
                                        {
                                            icon: IoPricetagOutline,
                                            label: "Min / hr",
                                            value: previewVenue.minPricePerHour ? `$${previewVenue.minPricePerHour}` : "—",
                                        },
                                    ].map(({ icon, label, value }) => (
                                        <VStack key={label} flex="1" spacing="1px" py={3} px={2}>
                                            <Icon as={icon} color="gray.400" boxSize={3.5} />
                                            <Text fontSize="9px" color="gray.400" textTransform="uppercase" letterSpacing="0.06em" fontWeight="600">
                                                {label}
                                            </Text>
                                            <Text fontSize="13px" fontWeight="700" color="gray.700">{value}</Text>
                                        </VStack>
                                    ))}
                                </HStack>

                                {/* Contact / links — only shown if the venue has any */}
                                {(previewVenue.website || previewVenue.contactEmail || previewVenue.contactPhone) && (
                                    <VStack
                                        align="stretch"
                                        spacing={1.5}
                                        bg="gray.50"
                                        borderRadius="xl"
                                        p={3}
                                        border="1px solid"
                                        borderColor="gray.100"
                                    >
                                        <Text
                                            fontSize="10px"
                                            fontWeight="700"
                                            color="gray.400"
                                            textTransform="uppercase"
                                            letterSpacing="0.06em"
                                        >
                                            Venue Contact
                                        </Text>
                                        {previewVenue.website && (
                                            <HStack
                                                as="a"
                                                href={
                                                    /^https?:\/\//i.test(previewVenue.website)
                                                        ? previewVenue.website
                                                        : `https://${previewVenue.website}`
                                                }
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                spacing={2}
                                                color="#226cf1"
                                                _hover={{ textDecoration: "underline" }}
                                            >
                                                <Icon as={IoOpenOutline} boxSize={4} />
                                                <Text fontSize="13px" fontWeight="600" noOfLines={1}>
                                                    {previewVenue.website.replace(/^https?:\/\//i, "")}
                                                </Text>
                                            </HStack>
                                        )}
                                        {previewVenue.contactEmail && (
                                            <HStack
                                                as="a"
                                                href={`mailto:${previewVenue.contactEmail}`}
                                                spacing={2}
                                                color="gray.700"
                                                _hover={{ color: "#226cf1" }}
                                            >
                                                <Icon as={IoMailOutline} boxSize={4} color="gray.400" />
                                                <Text fontSize="13px" noOfLines={1}>
                                                    {previewVenue.contactEmail}
                                                </Text>
                                            </HStack>
                                        )}
                                        {previewVenue.contactPhone && (
                                            <HStack
                                                as="a"
                                                href={`tel:${previewVenue.contactPhone}`}
                                                spacing={2}
                                                color="gray.700"
                                                _hover={{ color: "#226cf1" }}
                                            >
                                                <Icon as={IoCallOutline} boxSize={4} color="gray.400" />
                                                <Text fontSize="13px">{previewVenue.contactPhone}</Text>
                                            </HStack>
                                        )}
                                    </VStack>
                                )}

                                {/* ── Booking terms ───────────────────────────────── */}
                                {(() => {
                                    const pricePerHour = Number(previewVenue.minPricePerHour) || 0;
                                    const hours = computeEventHours(formData);
                                    const estimatedMin = hours * pricePerHour;

                                    return (
                                        <Box
                                            bg="blue.50"
                                            border="1px solid"
                                            borderColor="blue.100"
                                            borderRadius="xl"
                                            p={4}
                                        >
                                            <HStack spacing={2} mb={3}>
                                                <Icon as={IoInformationCircleOutline} color="#508cf7" boxSize={4} />
                                                <Text fontSize="xs" fontWeight="700" color="#508cf7" textTransform="uppercase" letterSpacing="0.05em">
                                                    How Partner Venues Work
                                                </Text>
                                            </HStack>

                                            <VStack align="stretch" spacing={3}>
                                                <HStack align="flex-start" spacing={3}>
                                                    <Flex
                                                        boxSize="26px"
                                                        bg="white"
                                                        borderRadius="full"
                                                        align="center"
                                                        justify="center"
                                                        flexShrink={0}
                                                        border="1px solid"
                                                        borderColor="blue.100"
                                                    >
                                                        <Icon as={IoCashOutline} color="#508cf7" boxSize={3.5} />
                                                    </Flex>
                                                    <Box flex="1">
                                                        <Text fontSize="13px" fontWeight="600" color="gray.800" lineHeight="1.3">
                                                            Hourly minimum
                                                        </Text>
                                                        <Text fontSize="12px" color="gray.600" lineHeight="1.5">
                                                            This venue is <b>{formatMoney(pricePerHour)}/hour</b>. Your minimum revenue goal is the hourly
                                                            rate × the number of hours you book the venue for.
                                                        </Text>
                                                    </Box>
                                                </HStack>

                                                <HStack align="flex-start" spacing={3}>
                                                    <Flex
                                                        boxSize="26px"
                                                        bg="white"
                                                        borderRadius="full"
                                                        align="center"
                                                        justify="center"
                                                        flexShrink={0}
                                                        border="1px solid"
                                                        borderColor="blue.100"
                                                    >
                                                        <Icon as={IoTimerOutline} color="#508cf7" boxSize={3.5} />
                                                    </Flex>
                                                    <Box flex="1">
                                                        <Text fontSize="13px" fontWeight="600" color="gray.800" lineHeight="1.3">
                                                            5-hour cutoff
                                                        </Text>
                                                        <Text fontSize="12px" color="gray.600" lineHeight="1.5">
                                                            Tickets sold must cover the minimum at least <b>5 hours before</b> your event starts.
                                                            If you hit the goal, the event is confirmed — otherwise ticket holders are refunded.
                                                        </Text>
                                                    </Box>
                                                </HStack>

                                                <HStack align="flex-start" spacing={3}>
                                                    <Flex
                                                        boxSize="26px"
                                                        bg="white"
                                                        borderRadius="full"
                                                        align="center"
                                                        justify="center"
                                                        flexShrink={0}
                                                        border="1px solid"
                                                        borderColor="blue.100"
                                                    >
                                                        <Icon as={IoWarningOutline} color="#508cf7" boxSize={3.5} />
                                                    </Flex>
                                                    <Box flex="1">
                                                        <Text fontSize="13px" fontWeight="600" color="gray.800" lineHeight="1.3">
                                                            End time required
                                                        </Text>
                                                        <Text fontSize="12px" color="gray.600" lineHeight="1.5">
                                                            Booking hours are based on your event's start and end time. Once you pick a partner venue,
                                                            end time becomes required so we can calculate your minimum.
                                                        </Text>
                                                    </Box>
                                                </HStack>
                                            </VStack>

                                            {/* Live calculation (only when schedule is already filled) */}
                                            {hours > 0 && (
                                                <Box
                                                    mt={4}
                                                    bg="white"
                                                    borderRadius="lg"
                                                    p={3}
                                                    border="1px solid"
                                                    borderColor="blue.100"
                                                >
                                                    <Text fontSize="10px" fontWeight="700" color="gray.400" textTransform="uppercase" letterSpacing="0.06em" mb={1}>
                                                        Your Estimated Minimum
                                                    </Text>
                                                    <HStack spacing={1} align="baseline">
                                                        <Text fontSize="md" fontWeight="700" color="gray.700">
                                                            {hours}h
                                                        </Text>
                                                        <Text fontSize="xs" color="gray.400">×</Text>
                                                        <Text fontSize="md" fontWeight="700" color="gray.700">
                                                            {formatMoney(pricePerHour)}
                                                        </Text>
                                                        <Text fontSize="xs" color="gray.400">=</Text>
                                                        <Text fontSize="lg" fontWeight="800" color="#508cf7">
                                                            {formatMoney(estimatedMin)}
                                                        </Text>
                                                    </HStack>
                                                </Box>
                                            )}
                                        </Box>
                                    );
                                })()}
                            </VStack>
                        </div>

                        {/* Footer */}
                        <div
                            style={{
                                backgroundColor: "#f7fafc",
                                borderTop: "1px solid #edf2f7",
                                padding: "12px 16px",
                                flexShrink: 0,
                                display: "flex",
                                gap: "8px",
                            }}
                        >
                            <Button variant="ghost" color="gray.600" size="sm" flex="1" onClick={onVenueClose}>
                                Cancel
                            </Button>
                            <Button
                                bg="#508cf7"
                                color="white"
                                size="sm"
                                flex="2"
                                borderRadius="lg"
                                _hover={{ bg: "#226cf1" }}
                                onClick={() => handleSelectPartnerVenue(previewVenue)}
                            >
                                Select This Venue
                            </Button>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </>
    );
};
